import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { SELL_ADJUSTMENT_MODES } from './packageOptionsModel';
import type { SellAdjustment, SellAdjustmentMode } from './packageOptionsModel';

const MODE_LABELS: Record<SellAdjustmentMode, string> = {
  none: 'No adjustment',
  fixed: 'Fixed amount (AUD)',
  daily: 'Daily amount (AUD/night)',
  pct: 'Percentage (%)',
};

type CopyFromOptionBannerProps = {
  onCopy: (adjustment: SellAdjustment) => void;
  sourceOptionLabel: string;
  targetOptionLabel: string;
};

export default function CopyFromOptionBanner({
  onCopy,
  sourceOptionLabel,
  targetOptionLabel,
}: CopyFromOptionBannerProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<SellAdjustmentMode>('none');
  const [amount, setAmount] = useState('');

  function handleApply() {
    onCopy({ mode, amount: Number(amount || 0) });
    setOpen(false);
    enqueueSnackbar(`Copied over package options from ${sourceOptionLabel} to ${targetOptionLabel}`, {
      variant: 'info',
    });
  }

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Button
        endIcon={
          <ExpandMoreIcon
            sx={{ ml: 'auto', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 120ms' }}
          />
        }
        fullWidth
        onClick={() => setOpen((current) => !current)}
        sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
      >
        → Copy all data from {sourceOptionLabel} as a starting point
      </Button>
      <Collapse in={open}>
        <Stack spacing={1.5} sx={{ borderTop: 1, borderColor: 'divider', p: 1.5 }}>
          <Typography color="text.secondary" variant="body2">
            This replaces all data on {targetOptionLabel} with a copy of {sourceOptionLabel}.
          </Typography>

          <Stack alignItems="center" direction="row" flexWrap="wrap" spacing={2}>
            <Typography variant="body2">Adjust sell prices:</Typography>
            <RadioGroup
              name="sell-adjustment-mode"
              onChange={(e) => setMode(e.target.value as SellAdjustmentMode)}
              row
              value={mode}
            >
              {SELL_ADJUSTMENT_MODES.map((option) => (
                <FormControlLabel
                  control={<Radio size="small" />}
                  key={option}
                  label={MODE_LABELS[option]}
                  slotProps={{ typography: { variant: 'body2' } }}
                  value={option}
                />
              ))}
            </RadioGroup>
            <TextField
              disabled={mode === 'none'}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.-]/g, ''))}
              placeholder={mode === 'pct' ? 'e.g. 10' : 'e.g. 100'}
              size="small"
              sx={{ width: 100 }}
              value={amount}
            />
            <Typography color="text.secondary" variant="body2">
              +/- to add or subtract
            </Typography>
            <Button color="primary" onClick={handleApply} size="small" variant="contained">
              Apply
            </Button>
          </Stack>
        </Stack>
      </Collapse>
    </Box>
  );
}
